// LootCouncilAiPanel.tsx
// ─────────────────────────────────────────────────────────────────────────────
// AI loot-advisor button + slide-in side drawer for the Loot Council screen.
//
// Self-contained:
//   - Owns its own fetch state, error handling, and in-memory cache.
//   - Only props it needs are raidId + the currently selected loot item.
//   - In-memory cache keyed by loot_item_id. Cache is lost on unmount (cheapest
//     option — no DB writes, no localStorage). Re-clicking the same item
//     within a session is free; switching items refetches.
//
// Mounted exactly once inside PlannerLootCouncilPanel.tsx, inside the
// "Current council focus" header next to the Clear-item button. Hidden when
// no item is selected.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { API_URL } from "./config";
import { buildLootAiWaitMessages } from "./plannerWaitMessages";
import { useRotatingWaitMessage } from "./useRotatingWaitMessage";

type Verdict = "strong_fit" | "solid_fit" | "marginal" | "poor_fit" | "caution";

type RankingRow = {
  character_id: string;
  character_name: string;
  rank: number;
  verdict: Verdict;
  reasons: string[];
  watch_out: string | null;
};

type AiResponse = {
  ranking: RankingRow[];
  overall_note: string | null;
  model: string | null;
  candidate_count: number;
};

type SelectedItem = {
  id: number;
  name: string;
  external_item_id?: string;
  slot?: string | null;
} | null;

type Props = {
  raidId: string;
  selectedItem: SelectedItem;
  /** Same gate as the Pick button — guild owner/officer, or this raid's RL/LM. */
  canAssign: boolean;
  /** Raid roster names — used for personalized wait lines (same pattern as Arc). */
  candidateNames?: string[];
  /** Optional className for the trigger button so the host can layout-match. */
  buttonClassName?: string;
};

// ── Verdict styling ─────────────────────────────────────────────────────────

const VERDICT_COPY: Record<Verdict, { label: string; tone: string }> = {
  strong_fit: { label: "Strong fit", tone: "border-amber-400/50 bg-amber-500/15 text-amber-800 dark:text-amber-100" },
  solid_fit:  { label: "Solid fit",  tone: "border-emerald-400/45 bg-emerald-500/12 text-emerald-800 dark:text-emerald-100" },
  marginal:   { label: "Marginal",   tone: "border-sky-400/40 bg-sky-500/10 text-sky-800 dark:text-sky-100" },
  poor_fit:   { label: "Poor fit",   tone: "border-[var(--c-line-strong)] bg-[var(--c-sunken)] text-[var(--c-ink-2)]" },
  caution:    { label: "Caution",    tone: "border-rose-400/45 bg-rose-500/12 text-rose-800 dark:text-rose-100" },
};

// ─────────────────────────────────────────────────────────────────────────────

export default function LootCouncilAiPanel({
  raidId,
  selectedItem,
  canAssign,
  candidateNames = [],
  buttonClassName,
}: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<AiResponse | null>(null);

  // In-memory cache: loot_item_id → response. Survives panel close/reopen,
  // dies on component unmount (parent route change).
  const cacheRef = useRef<Map<number, AiResponse>>(new Map());
  const inFlightRef = useRef<AbortController | null>(null);

  const waitMessages = useMemo(
    () => buildLootAiWaitMessages(candidateNames),
    [candidateNames]
  );
  const waitLine = useRotatingWaitMessage(loading, waitMessages);

  // If the council switches items while the panel is open, drop visible
  // state — the previous verdict no longer applies to the new selection.
  const lastItemIdRef = useRef<number | null>(selectedItem?.id ?? null);
  useEffect(() => {
    const newId = selectedItem?.id ?? null;
    if (lastItemIdRef.current !== newId) {
      lastItemIdRef.current = newId;
      setData(null);
      setErr(null);
      setLoading(false);
      if (inFlightRef.current) {
        inFlightRef.current.abort();
        inFlightRef.current = null;
      }
    }
  }, [selectedItem?.id]);

  const fetchAdvice = useCallback(async (lootItemId: number) => {
    // Cached?
    const cached = cacheRef.current.get(lootItemId);
    if (cached) {
      setData(cached);
      setErr(null);
      setLoading(false);
      return;
    }

    if (inFlightRef.current) inFlightRef.current.abort();
    const ctrl = new AbortController();
    inFlightRef.current = ctrl;

    setLoading(true);
    setErr(null);
    setData(null);
    try {
      const res = await fetch(
        `${API_URL}/raids/${encodeURIComponent(raidId)}/loot-council/ai-suggest`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ loot_item_id: lootItemId }),
          signal: ctrl.signal,
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.error || `HTTP ${res.status}`);
      }
      const result: AiResponse = {
        ranking: Array.isArray(body.ranking) ? body.ranking : [],
        overall_note: body.overall_note ?? null,
        model: body.model ?? null,
        candidate_count: Number(body.candidate_count) || 0,
      };
      cacheRef.current.set(lootItemId, result);
      setData(result);
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      setErr(typeof e?.message === "string" ? e.message : "Request failed");
    } finally {
      if (inFlightRef.current === ctrl) inFlightRef.current = null;
      setLoading(false);
    }
  }, [raidId]);

  const openPanel = useCallback(() => {
    if (!selectedItem) return;
    setOpen(true);
    void fetchAdvice(selectedItem.id);
  }, [selectedItem, fetchAdvice]);

  const closePanel = useCallback(() => {
    setOpen(false);
    if (inFlightRef.current) {
      inFlightRef.current.abort();
      inFlightRef.current = null;
    }
  }, []);

  // Esc closes the drawer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePanel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closePanel]);

  // No item, or viewer doesn't have council write powers → render nothing.
  // Host renders us inside the focus block which already shows guidance
  // when nothing is selected; view-only users simply don't see the button.
  if (!selectedItem || !canAssign) return null;

  const drawer = open
    ? createPortal(
        <div
          className="fixed inset-0 z-[95]"
          aria-modal="true"
          role="dialog"
          aria-label="AI loot advisor"
        >
          {/* Scrim */}
          <div
            className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
            onClick={closePanel}
          />
          {/* Drawer */}
          <aside
            className="absolute top-0 right-0 h-full w-full sm:w-[28rem] md:w-[32rem] flex flex-col
                       border-l border-violet-400/25 bg-[var(--c-surface)] shadow-[-12px_0_48px_rgba(0,0,0,0.55)]"
          >
            <header className="shrink-0 px-5 py-4 border-b border-[var(--c-line-soft)] bg-gradient-to-br from-violet-500/[0.10] via-[var(--c-surface)] to-[var(--c-surface)]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-800/85 dark:text-violet-200/85">
                    AI loot advisor
                  </div>
                  <div className="mt-1 text-sm font-semibold text-[var(--c-ink)] truncate">
                    {selectedItem.name}
                  </div>
                  <div className="mt-0.5 text-[11px] text-[var(--c-ink-3)]">
                    Comparing every candidate with this item (or its tier child) on their BIS list.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closePanel}
                  className="shrink-0 rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] px-2.5 py-1 text-[11px] font-bold uppercase text-[var(--c-ink-3)] hover:bg-[var(--c-sunken)] hover:text-[var(--c-ink)]"
                  aria-label="Close advisor"
                >
                  Close ✕
                </button>
              </div>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {loading && <LoadingState statusLine={waitLine} />}
              {!loading && err && <ErrorState message={err} onRetry={() => fetchAdvice(selectedItem.id)} />}
              {!loading && !err && data && data.ranking.length === 0 && (
                <EmptyState note={data.overall_note} />
              )}
              {!loading && !err && data && data.ranking.length > 0 && (
                <ResultsList data={data} />
              )}
            </div>

            <footer className="shrink-0 px-5 py-2.5 border-t border-[var(--c-line-soft)] text-[10px] text-[var(--c-ink-3)] leading-relaxed">
              <p className="m-0">
                AI guidance is advisory. Council still owns the decision. Don't use this for stat-cap math.
              </p>
              {data?.model ? (
                <p className="m-0 mt-1 font-mono text-[var(--c-ink-4)]">model: {data.model}</p>
              ) : null}
            </footer>
          </aside>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        type="button"
        onClick={openPanel}
        className={
          buttonClassName ||
          "shrink-0 rounded-lg border border-violet-400/40 bg-violet-500/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-violet-800 dark:text-violet-100 hover:bg-violet-500/25 hover:border-violet-300/60"
        }
        title="Get an AI breakdown comparing every candidate for this item"
      >
        ✨ AI breakdown
      </button>
      {drawer}
    </>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function LoadingState({ statusLine }: { statusLine: string }) {
  return (
    <div className="space-y-3">
      <div
        key={statusLine}
        className="text-[12px] text-violet-800/80 dark:text-violet-200/80 min-h-[1.25rem] leading-snug"
        style={{ animation: "lootAiMsgFadeIn 0.4s ease-out" }}
      >
        {statusLine || "Asking the model…"}
      </div>
      <style>{`
        @keyframes lootAiMsgFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="animate-pulse rounded-xl border border-[var(--c-line-soft)] bg-[var(--c-bg-veil)] h-24"
        />
      ))}
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3.5">
      <div className="text-[11px] font-bold uppercase tracking-wider text-rose-800 dark:text-rose-200">
        Couldn't get advice
      </div>
      <p className="mt-1.5 text-sm text-rose-800/90 dark:text-rose-100/90 leading-relaxed break-words">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 rounded-lg border border-[var(--c-line-strong)] bg-[var(--c-sunken)] px-3 py-1.5 text-[10px] font-bold uppercase text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)]"
      >
        Try again
      </button>
    </div>
  );
}

function EmptyState({ note }: { note: string | null }) {
  return (
    <div className="rounded-xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-4 text-sm text-[var(--c-ink-2)]">
      {note || "No candidates to compare."}
    </div>
  );
}

function ResultsList({ data }: { data: AiResponse }) {
  return (
    <>
      {data.overall_note ? (
        <div className="rounded-xl border border-violet-400/25 bg-violet-500/[0.08] px-3.5 py-2.5 text-[12px] text-violet-800/90 dark:text-violet-100/90 leading-relaxed">
          {data.overall_note}
        </div>
      ) : null}
      {data.ranking.map((row) => (
        <ResultCard key={row.character_id} row={row} />
      ))}
    </>
  );
}

function ResultCard({ row }: { row: RankingRow }) {
  const verdict = VERDICT_COPY[row.verdict] || VERDICT_COPY.marginal;
  return (
    <article className="rounded-xl border border-[var(--c-line-soft)] bg-gradient-to-b from-[var(--c-bg-veil)] to-[var(--c-surface)] p-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
      <header className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex h-6 min-w-[1.75rem] items-center justify-center rounded-full border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-2 text-[11px] font-bold tabular-nums text-[var(--c-ink-2)]">
            #{row.rank}
          </span>
          <span className="font-semibold text-[var(--c-ink)] truncate">{row.character_name}</span>
        </div>
        <span
          className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${verdict.tone}`}
        >
          {verdict.label}
        </span>
      </header>
      {row.reasons.length > 0 ? (
        <ul className="list-disc pl-5 m-0 space-y-1 text-[12.5px] leading-snug">
        {row.reasons.map((r, i) => (
          <li key={i} className="text-[var(--c-ink)]">{r}</li>
        ))}
      </ul>
      ) : null}
      {row.watch_out ? (
        <div className="mt-2.5 rounded-lg border border-amber-400/30 bg-amber-500/10 px-2.5 py-1.5 text-[11.5px] text-amber-800 dark:text-amber-100">
          <span className="font-bold uppercase tracking-wider text-[10px] text-amber-800 dark:text-amber-200 mr-1.5">
            Watch out
          </span>
          {row.watch_out}
        </div>
      ) : null}
    </article>
  );
}
